import { useEffect } from "react";
import type { AuthCompany } from "./use-auth";

// Converts a hex color (#rrggbb or #rgb) into an "H S% L%" triplet
// compatible with the app's `hsl(...)` CSS custom properties.
function hexToHslTriplet(hex: string): string | null {
  const clean = hex.trim().replace(/^#/, "");
  const full = clean.length === 3
    ? clean.split("").map((c) => c + c).join("")
    : clean;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;

  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h *= 60;
  }

  return `${h.toFixed(1)} ${(s * 100).toFixed(1)}% ${(l * 100).toFixed(1)}%`;
}

// White text only when the fill is dark enough for WCAG-ish contrast.
// HSL lightness mis-classifies mint (#00C4A7) as dark; use relative luminance.
function foregroundForHex(hex: string): string {
  const clean = hex.trim().replace(/^#/, "");
  const full = clean.length === 3 ? clean.split("").map((c) => c + c).join("") : clean;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return "0 0% 100%";
  const channels = [0, 2, 4].map((i) => {
    const c = parseInt(full.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  return luminance > 0.18 ? "235.3 100% 10%" : "0 0% 100%";
}

const OVERRIDE_VARS = [
  "--primary",
  "--primary-foreground",
  "--ring",
  "--sidebar-primary",
  "--sidebar-primary-foreground",
  "--sidebar-ring",
  "--chart-1",
];

// Colors shipped as the old platform default (schema + settings form).
// They are not a deliberate white-label choice, so the AqarOS tokens win
// until a company saves a different palette. No data migration required.
const LEGACY_PRIMARY = new Set(["#3b82f6", "#2563eb"]);
const LEGACY_ACCENT = new Set(["#4f46e5"]);

function normalizeHex(value: string): string {
  const clean = value.trim().toLowerCase();
  if (/^#[0-9a-f]{3}$/.test(clean)) {
    return `#${clean[1]}${clean[1]}${clean[2]}${clean[2]}${clean[3]}${clean[3]}`;
  }
  return clean;
}

function isPlatformDefault(value: string | null | undefined, legacy: Set<string>): boolean {
  if (!value) return true;
  return legacy.has(normalizeHex(value));
}

const ACCENT_OVERRIDE_VARS = ["--accent", "--accent-foreground", "--chart-2", "--sidebar-accent-foreground"];

/**
 * Applies a company's white-label branding (primaryColor / accentColor) to the
 * document root as CSS custom-property overrides. Missing colors and the old
 * platform blues fall back to the AqarOS tokens in index.css. Focus rings stay
 * mint. Call with `undefined`/`null` to clear overrides (e.g. on logout).
 */
export function useCompanyTheme(company?: AuthCompany | null) {
  useEffect(() => {
    const root = document.documentElement;

    if (company?.primaryColor && !isPlatformDefault(company.primaryColor, LEGACY_PRIMARY)) {
      const hsl = hexToHslTriplet(company.primaryColor);
      if (hsl) {
        const fg = foregroundForHex(company.primaryColor);
        root.style.setProperty("--primary", `hsl(${hsl})`);
        root.style.setProperty("--primary-foreground", `hsl(${fg})`);
        root.style.setProperty("--sidebar-primary", `hsl(${hsl})`);
        root.style.setProperty("--sidebar-primary-foreground", `hsl(${fg})`);
        root.style.setProperty("--chart-1", `hsl(${hsl})`);
      }
    } else {
      OVERRIDE_VARS.forEach((v) => root.style.removeProperty(v));
    }

    if (company?.accentColor && !isPlatformDefault(company.accentColor, LEGACY_ACCENT)) {
      const hsl = hexToHslTriplet(company.accentColor);
      if (hsl) {
        const fg = foregroundForHex(company.accentColor);
        root.style.setProperty("--accent", `hsl(${hsl})`);
        root.style.setProperty("--accent-foreground", `hsl(${fg})`);
        root.style.setProperty("--chart-2", `hsl(${hsl})`);
        root.style.setProperty("--sidebar-accent-foreground", `hsl(${hsl})`);
      }
    } else {
      ACCENT_OVERRIDE_VARS.forEach((v) => root.style.removeProperty(v));
    }

    if (company?.name) {
      document.title = `${company.name} — ${document.title.split("—").pop()?.trim() || "Rently"}`;
    }

    return () => {
      OVERRIDE_VARS.forEach((v) => root.style.removeProperty(v));
      ACCENT_OVERRIDE_VARS.forEach((v) => root.style.removeProperty(v));
    };
  }, [company?.primaryColor, company?.accentColor, company?.name]);
}
